import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  AttachExperiencePayload,
  CreateBundlePayload,
  CreateTicketPayload,
  UpdateBundleContentPayload,
  UpdateBundleOfferPayload,
  UpdateBundlePayload,
  UpdateBundlePricingPayload,
  UpdateBundleRulesPayload,
  UpdateTicketPayload,
  attachExperience,
  createBundle,
  createTicket,
  deleteBundle,
  getBundle,
  getCandidateTickets,
  listBundles,
  removeExperience,
  removeTicket,
  updateBundle,
  updateBundleContent,
  updateBundleOffer,
  updateBundlePricing,
  updateBundleRules,
  updateTicket,
} from '~/services/AccommodationService';

export const bundleQueryKeys = {
  all: ['bundles'] as const,
  lists: () => [...bundleQueryKeys.all, 'list'] as const,
  list: (filters: { limit?: number; page?: number }) => [...bundleQueryKeys.lists(), filters] as const,
  details: () => [...bundleQueryKeys.all, 'detail'] as const,
  detail: (bundleId: string) => [...bundleQueryKeys.details(), bundleId] as const,
  candidateTickets: (bundleId: string, offerId: string) =>
    [...bundleQueryKeys.all, 'candidate-tickets', bundleId, offerId] as const,
};

interface UseGetBundlesParams {
  limit?: number;
  page?: number;
  enabled?: boolean;
}

export function useGetBundles({ limit, page, enabled = true }: UseGetBundlesParams = {}) {
  return useQuery({
    queryKey: bundleQueryKeys.list({ limit, page }),
    queryFn: async ({ signal }) => {
      const response = await listBundles({ limit, page, signal });
      return response.result;
    },
    enabled,
  });
}

export function useGetAllBundles({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bundleQueryKeys.list({ limit: 1000, page: 1 }),
    queryFn: async ({ signal }) => {
      const response = await listBundles({ limit: 1000, page: 1, signal });
      return response.result;
    },
    enabled,
  });
}

interface UseGetBundleParams {
  bundleId: string;
  enabled?: boolean;
}

export function useGetBundle({ bundleId, enabled = true }: UseGetBundleParams) {
  return useQuery({
    queryKey: bundleQueryKeys.detail(bundleId),
    queryFn: async ({ signal }) => {
      const response = await getBundle({ bundleId, signal });
      return response.result;
    },
    enabled: enabled && !!bundleId,
  });
}

export function useUpdateBundlePricing() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ bundleId, payload }: { bundleId: string; payload: UpdateBundlePricingPayload }) => {
      return updateBundlePricing({ bundleId, payload });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.detail(variables.bundleId) });
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.lists() });
      enqueueSnackbar('Bundle pricing updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update pricing: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useUpdateBundleRules() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ bundleId, payload }: { bundleId: string; payload: UpdateBundleRulesPayload }) => {
      return updateBundleRules({ bundleId, payload });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.detail(variables.bundleId) });
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.lists() });
      enqueueSnackbar('Bundle rules updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update rules: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useUpdateBundleContent() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ bundleId, payload }: { bundleId: string; payload: UpdateBundleContentPayload }) => {
      return updateBundleContent({ bundleId, payload });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.detail(variables.bundleId) });
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.lists() });
      enqueueSnackbar('Bundle content updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update content: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useUpdateBundleOffer() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      bundleId,
      offerId,
      payload,
    }: {
      bundleId: string;
      offerId: string;
      payload: UpdateBundleOfferPayload;
    }) => {
      return updateBundleOffer({ bundleId, offerId, payload });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.detail(variables.bundleId) });
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.lists() });
      enqueueSnackbar('Bundle offer updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update offer: ${error.message}`, { variant: 'error' });
    },
  });
}

// Bundle CRUD (HP-2379)

export function useCreateBundle() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      payload,
      errorBodyCallback,
    }: {
      payload: CreateBundlePayload;
      errorBodyCallback?: (body: unknown) => void;
    }) => {
      const response = await createBundle({ payload, errorBodyCallback });
      return response.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.lists() });
      enqueueSnackbar('Bundle created', { variant: 'success' });
    },
    onError: (error: Error) => {
      // 409 conflicts are surfaced inline on the create page via conflictingPropertyIds.
      const status = (error as { status?: number }).status;
      if (status === 409) return;
      enqueueSnackbar(`Failed to create bundle: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useUpdateBundle() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      bundleId,
      payload,
      errorBodyCallback,
    }: {
      bundleId: string;
      payload: UpdateBundlePayload;
      errorBodyCallback?: (body: unknown) => void;
    }) => {
      return updateBundle({ bundleId, payload, errorBodyCallback });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.detail(variables.bundleId) });
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.lists() });
      enqueueSnackbar('Bundle updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      // 409 conflicts are surfaced inline by callers that handle conflictingPropertyIds.
      const status = (error as { status?: number }).status;
      if (status === 409) return;
      enqueueSnackbar(`Failed to update bundle: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useDeleteBundle() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ bundleId }: { bundleId: string }) => {
      return deleteBundle({ bundleId });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.detail(variables.bundleId) });
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.lists() });
      enqueueSnackbar('Bundle deleted', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to delete bundle: ${error.message}`, { variant: 'error' });
    },
  });
}

// Experience-level (HP-2379)

export function useAttachExperience() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ bundleId, payload }: { bundleId: string; payload: AttachExperiencePayload }) => {
      const response = await attachExperience({ bundleId, payload });
      return response.result;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.detail(variables.bundleId) });
      enqueueSnackbar('Experience attached', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to attach experience: ${error.message}`, { variant: 'error' });
    },
  });
}

interface UseGetCandidateTicketsParams {
  bundleId: string;
  offerId: string;
  enabled?: boolean;
}

export function useGetCandidateTickets({ bundleId, offerId, enabled = true }: UseGetCandidateTicketsParams) {
  return useQuery({
    queryKey: bundleQueryKeys.candidateTickets(bundleId, offerId),
    queryFn: async ({ signal }) => {
      const response = await getCandidateTickets({ bundleId, offerId, signal });
      return response.result;
    },
    enabled: enabled && !!bundleId && !!offerId,
  });
}

export function useRemoveExperience() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ bundleId, offerId }: { bundleId: string; offerId: string }) => {
      return removeExperience({ bundleId, offerId });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.detail(variables.bundleId) });
      enqueueSnackbar('Experience removed', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to remove experience: ${error.message}`, { variant: 'error' });
    },
  });
}

// Ticket-level (HP-2379)

export function useCreateTicket() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      bundleId,
      offerId,
      payload,
    }: {
      bundleId: string;
      offerId: string;
      payload: CreateTicketPayload;
    }) => {
      const response = await createTicket({ bundleId, offerId, payload });
      return response.result;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.detail(variables.bundleId) });
      enqueueSnackbar('Ticket created', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to create ticket: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useUpdateTicket() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      bundleId,
      offerId,
      ticketId,
      payload,
    }: {
      bundleId: string;
      offerId: string;
      ticketId: string;
      payload: UpdateTicketPayload;
    }) => {
      return updateTicket({ bundleId, offerId, ticketId, payload });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.detail(variables.bundleId) });
      enqueueSnackbar('Ticket updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update ticket: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useRemoveTicket() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ bundleId, offerId, ticketId }: { bundleId: string; offerId: string; ticketId: string }) => {
      return removeTicket({ bundleId, offerId, ticketId });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.detail(variables.bundleId) });
      enqueueSnackbar('Ticket removed', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to remove ticket: ${error.message}`, { variant: 'error' });
    },
  });
}
